import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import debounce from "lodash.debounce";
import {
  MagnifyingGlassIcon,
  PhotoIcon,
  TrashIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { searchIcons } from "../../../api/queries";
import { useLanguage } from "../../../contexts/LanguageContext";
import NumberInput from "../../../components/NumberInput";
import { finalMilestoneIndex } from "../../../utils/passUtils";
import Tooltip from "../../../components/Tooltip";
import logo from "../../../assets/logo.png";

// Split-pane milestone editor: a persistent "Global Stamp Library" (left) that
// feeds a "Reward Journey Builder" (right). Clicking a library icon assigns it to
// the currently active target — either the card's default stamp icon or a
// milestone switched to "Unique Icon". Self-contained icon search; upload + crop
// are delegated to the parent (same flow as the single-reward stamp icon).
export default function RewardJourneyBuilder({
  milestones = [],
  stampCount,
  stampIcon,
  stampIconColor,
  initialPoints,
  milestoneErrors = {},
  onSetInitialPoints,
  onUpdateMilestone,
  onAddMilestone,
  onRemoveMilestone,
  onSetStampIcon,
  onSetStampIconColor,
  onUploadStampIcon,
  onUploadMilestoneIcon,
}) {
  const { t } = useLanguage();
  const uploadInputRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState("");
  // Which stamp the merchant is actively assigning an icon to. `null` = nothing
  // armed (the resting state): no highlight anywhere, so editing an unrelated
  // field never looks like it has focus. A pick with nothing armed falls back to
  // the default icon (see assignIcon), keeping the library from ever dead-ending.
  const [activeTarget, setActiveTarget] = useState(null);

  const debouncedSetSearchQuery = useMemo(
    () => debounce((value) => setSearchQuery(value), 300),
    [],
  );

  // Flush the pending debounced call on unmount so a trailing invocation can't
  // setState after the component is gone (navigating away within the 300ms).
  useEffect(
    () => () => debouncedSetSearchQuery.cancel(),
    [debouncedSetSearchQuery],
  );

  const { data: iconResponse, isLoading } = useQuery({
    queryKey: ["journey-icons", searchQuery],
    queryFn: () => searchIcons(searchQuery),
    enabled: searchQuery.trim().length > 0,
  });

  const assignIcon = (url) => {
    if (activeTarget?.type === "milestone") {
      onUpdateMilestone(activeTarget.id, { icon: url });
    } else {
      onSetStampIcon(url);
      setActiveTarget({ type: "default" }); // arm default so the pick is visible
    }
  };

  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (activeTarget?.type === "milestone") {
      onUploadMilestoneIcon(file, activeTarget.id);
      e.target.value = "";
    } else {
      onUploadStampIcon(e);
      setActiveTarget({ type: "default" });
    }
  };

  // Disarm if the armed milestone was removed (no milestone carries its id
  // anymore), dropping back to the resting state. Keying on the stable id
  // instead of an array index means a delete/reorder of *other* milestones
  // keeps the armed one armed rather than silently retargeting a neighbour.
  useEffect(() => {
    if (activeTarget?.type !== "milestone") return;
    if (!milestones.some((m) => m._uid === activeTarget.id))
      setActiveTarget(null);
  }, [activeTarget, milestones]);

  // `effectiveTarget` is where a pick actually lands (defaults to the default
  // icon when nothing is selected). The uploader preview + library "selected"
  // state follow it so they always show where a pick would go.
  const effectiveTarget = activeTarget ?? { type: "default" };
  const isDefaultTarget = effectiveTarget.type === "default";
  const effectiveMilestone = isDefaultTarget
    ? null
    : milestones.find((m) => m._uid === effectiveTarget.id);
  const effectiveIcon = isDefaultTarget ? stampIcon : effectiveMilestone?.icon;
  const selectValue =
    activeTarget?.type === "milestone" ? activeTarget.id : "default";

  // Shared with the save-time validation message, so the row a notification
  // names is always the row this builder heads "Final Reward".
  const finalIndex = finalMilestoneIndex(milestones);

  // "How many stamps needed for a reward?" edits the final reward's position
  // (the card length) from the overview, so the Final Reward card's own stamp
  // field is read-only. Its floor is one above the highest intermediate reward,
  // so lowering the goal can never drop it onto/under another milestone.
  const finalMilestone = finalIndex >= 0 ? milestones[finalIndex] : null;
  const highestIntermediate = milestones.reduce(
    (max, m, i) => (i === finalIndex ? max : Math.max(max, Number(m.position) || 0)),
    0,
  );
  const rewardGoalMin = Math.max(highestIntermediate + 1, 1);

  const setRewardGoal = (value) => {
    if (finalMilestone) onUpdateMilestone(finalMilestone._uid, { position: value });
  };

  return (
    <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(180px,240px)_1fr]">
      {/* ---- Global Stamp Library ---- */}
      {/* Sticky (like the card preview) so it follows the scroll instead of
          stranding far above once several milestones stretch the right column.
          `self-start` stops the grid from stretching it to the row height (which
          would leave sticky no room to move); it scrolls internally if taller
          than the viewport. */}
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:self-start lg:overflow-y-auto">
        <h3 className="text-sm font-bold text-gray-900">
          {t("passes.form.journeyBuilder.libraryTitle")}
        </h3>

        {/* Target selector — which stamp the library feeds. Co-located with the
            icon grid so "which stamp" and "which icon" happen in one place, and
            only lists valid targets (default + milestones set to Unique Icon). */}
        <div className="mt-3">
          <label
            htmlFor="stamp-target"
            className="block text-[10px] font-semibold uppercase tracking-wide text-gray-500"
          >
            {t("passes.form.journeyBuilder.editingLabel")}
          </label>
          <div className="mt-1">
            <select
              id="stamp-target"
              value={selectValue}
              onChange={(e) =>
                setActiveTarget(
                  e.target.value === "default"
                    ? { type: "default" }
                    : { type: "milestone", id: e.target.value },
                )
              }
              className="block w-full rounded-md border-gray-300 text-sm shadow-xs"
            >
              <option value="default">
                {t("passes.form.journeyBuilder.defaultIcon")}
              </option>
              {milestones.map((milestone, i) => (
                <option key={milestone._uid} value={milestone._uid}>
                  {i === finalIndex
                    ? t("passes.form.journeyBuilder.finalReward")
                    : t("passes.form.journeyBuilder.milestone", {
                        number: i + 1,
                      })}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="relative mt-3">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            defaultValue={searchQuery}
            onChange={(e) => debouncedSetSearchQuery(e.target.value)}
            placeholder={t("passes.form.journeyBuilder.searchPlaceholder")}
            className="block w-full rounded-md border-gray-300 pl-9 text-sm shadow-xs"
          />
        </div>

        <div className="mt-3 max-h-[22rem] overflow-y-auto pr-1">
          {searchQuery.trim().length === 0 ? (
            <p className="py-8 text-center text-xs text-gray-400">
              {t("passes.form.milestones.iconModal.searchHint")}
            </p>
          ) : isLoading ? (
            <p className="py-8 text-center text-xs text-gray-400">
              {t("passes.form.stampIcon.loading")}
            </p>
          ) : iconResponse?.data?.length ? (
            <div className="grid grid-cols-3 gap-2">
              {iconResponse.data.map((icon) => {
                // Some provider results carry no thumbnail; skip them rather
                // than dereference [0].url and crash the whole editor (this
                // subtree sits outside any RecoverableBoundary).
                const url = icon.thumbnails?.[0]?.url;
                if (!url) return null;
                const selected = isDefaultTarget
                  ? stampIcon === url
                  : effectiveMilestone?.icon === url;
                return (
                  <button
                    key={icon.id}
                    type="button"
                    onClick={() => assignIcon(url)}
                    title={icon.name}
                    className={`flex aspect-square items-center justify-center rounded-lg border bg-white p-2 transition ${
                      selected
                        ? "border-indigo-500 ring-2 ring-indigo-200"
                        : "border-gray-200 hover:border-indigo-400 hover:bg-indigo-50"
                    }`}
                  >
                    <img
                      src={url}
                      alt={icon.name}
                      className="h-full w-full object-contain"
                    />
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="py-8 text-center text-xs text-gray-400">
              {t("passes.form.stampIcon.noResults")}
            </p>
          )}
        </div>

        {/* Upload a custom stamp icon — assigned to the active target, same as
            a library pick. Classic dashed-box uploader with a live preview. */}
        <div className="mt-3 border-t border-gray-200 pt-3">
          <label className="block text-sm font-medium text-gray-700">
            {t("passes.form.stampIcon.uploadCustom")}
          </label>
          <div className="mt-1 flex justify-center rounded-md border-2 border-dashed border-gray-300 px-6 pb-2 pt-3">
            <div className="text-center">
              {effectiveIcon ? (
                <img
                  src={effectiveIcon}
                  alt=""
                  className="mx-auto h-16 w-16 rounded-full"
                />
              ) : (
                <PhotoIcon className="mx-auto h-16 w-16 text-gray-400" />
              )}
              <div className="mt-2 text-sm text-gray-600">
                <button
                  type="button"
                  onClick={() => uploadInputRef.current?.click()}
                  className="cursor-pointer rounded-md bg-white font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-hidden focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2"
                >
                  {effectiveIcon
                    ? t("passes.form.stampIcon.change")
                    : t("passes.form.stampIcon.uploadButton")}
                </button>
                <input
                  ref={uploadInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleUpload}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ---- Reward Overview + milestones ---- */}
      <div>
        {/* Reward overview */}
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
          <p className="text-sm font-semibold text-gray-900">
            {t("passes.form.journeyBuilder.overview")}
          </p>
          <p className="text-sm text-gray-600">
            {t("passes.form.journeyBuilder.summary", {
              stamps: stampCount,
              rewards: milestones.length,
            })}
          </p>

          <div className="mt-3 space-y-3 border-t border-gray-200 pt-3">
            <div>
              <div className="flex items-center">
                <label className="block text-sm font-medium text-gray-700">
                  {t("passes.form.initialStamps.label")}
                </label>
                <Tooltip
                  text={t("passes.form.initialStamps.tooltip")}
                  className="ml-1"
                />
              </div>
              <div className="mt-1">
                <NumberInput
                  value={initialPoints}
                  onChange={(value) => onSetInitialPoints(value)}
                  min={0}
                  max={stampCount}
                />
              </div>
            </div>

            {/* Card length = the final reward's position. Editing it here keeps
                the Final Reward card's own stamp field read-only. */}
            <div>
              <div className="flex items-center">
                <label className="block text-sm font-medium text-gray-700">
                  {t("passes.form.journeyBuilder.rewardGoal")}
                </label>
                <Tooltip
                  text={t("passes.form.journeyBuilder.rewardGoalTooltip")}
                  className="ml-1"
                />
              </div>
              <div className="mt-1">
                <NumberInput
                  value={stampCount}
                  onChange={setRewardGoal}
                  min={rewardGoalMin}
                  max={18}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Default stamp: the icon target + the background shared by every
            stamp (a milestone overrides only the icon, not this background).
            Clicking the row arms the default icon as the library target. */}
        <div className="mt-3 rounded-xl border border-gray-200">
          <button
            type="button"
            onClick={() => setActiveTarget({ type: "default" })}
            className="flex w-full items-center gap-3 rounded-t-xl p-3 text-left"
          >
            <span
              className={`flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-white ${
                activeTarget?.type === "default"
                  ? "border-indigo-500 ring-2 ring-indigo-200"
                  : "border-gray-200"
              }`}
            >
              {/* No custom icon chosen yet → show the Loyaltify default icon,
                  which is exactly what the wallet renders as the fallback
                  (stampIconSource), instead of an empty "+" placeholder. */}
              <img
                src={stampIcon || logo}
                alt=""
                className="h-8 w-8 object-contain"
              />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold text-gray-900">
                {t("passes.form.journeyBuilder.defaultIcon")}
              </span>
              <span className="block text-xs text-gray-500">
                {t("passes.form.journeyBuilder.defaultIconHint")}
              </span>
            </span>
          </button>

          <div className="flex items-center justify-between border-t border-gray-100 px-3 py-2.5">
            <label
              htmlFor="stamp-bg-color"
              className="text-sm font-medium text-gray-700"
            >
              {t("passes.form.stampIcon.backgroundColor")}
            </label>
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase text-gray-400">
                {stampIconColor || "#ffffff"}
              </span>
              <input
                id="stamp-bg-color"
                type="color"
                value={stampIconColor || "#ffffff"}
                onChange={(e) => onSetStampIconColor(e.target.value)}
                className="h-8 w-12 shrink-0 cursor-pointer rounded-md border border-gray-300 p-0.5"
              />
            </div>
          </div>
        </div>

        {/* Add reward — sits directly under the default stamp so new rewards
            appear above the Final Reward, which the sort keeps pinned last. */}
        <button
          type="button"
          onClick={onAddMilestone}
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-indigo-300 bg-indigo-50 px-4 py-3 text-sm font-semibold text-indigo-600 hover:border-indigo-400 hover:bg-indigo-100"
        >
          <PlusIcon className="h-5 w-5" />
          {t("passes.form.journeyBuilder.addMilestone")}
        </button>

        {/* Milestone cards */}
        <div className="mt-3 space-y-3">
          {milestones.map((milestone, index) => {
            const active =
              activeTarget?.type === "milestone" &&
              activeTarget.id === milestone._uid;
            return (
              <div
                key={milestone._uid}
                className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
              >
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-bold text-gray-900">
                    {index === finalIndex
                      ? t("passes.form.journeyBuilder.finalReward")
                      : t("passes.form.journeyBuilder.milestone", {
                          number: index + 1,
                        })}
                  </h4>
                  <button
                    type="button"
                    onClick={() => onRemoveMilestone(milestone._uid)}
                    disabled={milestones.length <= 1}
                    className="text-gray-400 hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label={t("passes.form.milestones.remove")}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>

                {/* Reward icon — styled like the default-stamp row. Click to arm
                    this reward as the library target; the ring marks the active
                    one. Falls back to the default stamp icon, then the Loyaltify
                    logo, mirroring the wallet. */}
                <button
                  type="button"
                  onClick={() =>
                    setActiveTarget({ type: "milestone", id: milestone._uid })
                  }
                  className={`mt-3 flex w-full items-center gap-3 rounded-lg border p-3 text-left ${
                    active
                      ? "border-indigo-500 ring-2 ring-indigo-200"
                      : "border-gray-200"
                  }`}
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-md border border-gray-200 bg-white">
                    <img
                      src={milestone.icon || stampIcon || logo}
                      alt=""
                      className="h-8 w-8 object-contain"
                    />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-gray-900">
                      {index === finalIndex
                        ? t("passes.form.journeyBuilder.finalRewardStamp")
                        : t("passes.form.journeyBuilder.milestoneStamp")}
                    </span>
                    <span className="block text-xs text-gray-500">
                      {t("passes.form.journeyBuilder.rewardIconHint")}
                    </span>
                  </span>
                </button>

                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-[9rem_1fr]">
                  <div>
                    <label className="block text-xs font-medium text-gray-600">
                      {t("passes.form.milestones.stampLabel")}
                    </label>
                    <div className="mt-1">
                      <NumberInput
                        value={milestone.position}
                        onChange={(value) =>
                          onUpdateMilestone(milestone._uid, { position: value })
                        }
                        min={1}
                        // An intermediate reward must always land before the
                        // Final Reward, so it's capped one stamp below the card
                        // length. The Final Reward itself is driven by the
                        // "reward goal" field in the overview above.
                        max={index === finalIndex ? 18 : stampCount - 1}
                        disabled={index === finalIndex}
                        hasError={!!milestoneErrors[index]?.position}
                      />
                    </div>
                    {milestoneErrors[index]?.position && (
                      <p className="mt-1 text-xs text-red-600">
                        {t(
                          milestoneErrors[index].position === "duplicate"
                            ? "passes.form.milestones.positionDuplicate"
                            : "passes.form.milestones.positionInvalid",
                        )}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600">
                      {t("passes.form.milestones.rewardLabel")}
                    </label>
                    <input
                      type="text"
                      value={milestone.label}
                      onChange={(e) =>
                        onUpdateMilestone(milestone._uid, {
                          label: e.target.value,
                        })
                      }
                      placeholder={t(
                        "passes.form.milestones.rewardPlaceholder",
                      )}
                      className={`mt-1 block w-full rounded-md shadow-xs sm:text-sm ${
                        milestoneErrors[index]?.label
                          ? "border-red-400 focus:border-red-500 focus:ring-red-500"
                          : "border-gray-300"
                      }`}
                    />
                    {milestoneErrors[index]?.label && (
                      <p className="mt-1 text-xs text-red-600">
                        {t("passes.form.milestones.labelRequired")}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <p className="mt-3 text-xs text-gray-500">
          {t("passes.form.milestones.finalNote", { count: stampCount })}
        </p>
      </div>
    </div>
  );
}
